import * as z from "zod";
import { userFacingError } from "../authorization";
import { connectorTypeOf } from "../connectors";
import { connectorDestinationAuditMetadata } from "../connectors/auditDestination";
import { normalizedConnectorIdentity } from "../connectors/normalizeUrlPattern";
import {
  createOAuthConnectionSchema,
  insertFreshOAuthConnection,
} from "./freshOAuthConnection";
import { mutator } from "./mutator";
import { generateId } from "../id";
import { stringRecord } from "./utils";

export const switchConnectorVariantSchema = z.object({
  connectedAppId: z.string(),
  identifier: z.string(),
  name: z.string(),
  urlPattern: z.string(),
  configuration: stringRecord,
  createOAuthConnection: createOAuthConnectionSchema.optional(),
});

export const switchConnectorVariant = mutator({
  schema: switchConnectorVariantSchema,
  resolve: {
    app: { kind: "connectedApp", from: "connectedAppId" },
  },
  permissions: [
    {
      permission: "connected_app:write",
      scopeId: ({ args }) => args.app.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      app,
      identifier,
      name,
      urlPattern,
      configuration,
      createOAuthConnection,
    },
  }) => {
    if (!app.fresh) {
      throw userFacingError("Connector has already been saved.");
    }

    const cleanedIdentifier = identifier.trim();
    const type = connectorTypeOf(app.identifier);
    if (!type?.variants.some((v) => v.identifier === cleanedIdentifier)) {
      throw userFacingError("Not a variant of the same product.");
    }

    if (app.oauthConnectionId) {
      await tx.mutate.oauthConnections.delete({ id: app.oauthConnectionId });
    }

    let oauthId: string | null = null;
    if (createOAuthConnection) {
      oauthId = generateId();
      await insertFreshOAuthConnection(
        tx,
        createOAuthConnection,
        oauthId,
        ctx.tenantId
      );
    }

    const identity = normalizedConnectorIdentity({
      name,
      identifier,
      urlPattern,
      configuration,
    });
    await tx.mutate.connectedApps.update({
      id: app.id,
      ...identity,
      oauthConnectionId: oauthId,
      oauthClientId: createOAuthConnection?.clientId ?? "",
      hasOAuthClientSecret: false,
      oauthRequestedScopes: createOAuthConnection?.scopes ?? [],
      lastTestAt: null,
      lastTestStatus: null,
      lastTestMessage: null,
    });
    ctx.recordAuditMetadata?.(connectorDestinationAuditMetadata(app, identity));
  },
});
